import { Inject, Injectable, inject, InjectionToken, OnDestroy } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { DOCUMENT } from '@angular/common';
import { GTMStart, WindowDataLayer } from './gtm.models';
import { AnalyticsEcommerceGTMSettings } from 'analytics-api';


export const WINDOW = new InjectionToken<Window>(
  'An abstraction over global window object',
  {
    factory: () => {
      const { defaultView } = inject(DOCUMENT);

      if (!defaultView) {
        throw new Error('Window is not available');
      }

      return defaultView;
    },
  },
);

@Injectable({
  providedIn: 'root'
})
export class GoogleTagManagerService implements OnDestroy {

  public isLoaded$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  private isLoaded = false;
  private isLoading = false;
  private gtmId: string = '';
  private eventsToLoad: AnalyticsEcommerceGTMSettings[] = [];
  private allowMultiplesScripts = false;
  private readonly clearSubscriptions$: Subject<void> = new Subject();

  constructor(
    @Inject(DOCUMENT) private document: Document,
    @Inject(WINDOW) private window: WindowDataLayer
  ) {}

  public ngOnDestroy(): void {
    this.clearSubscriptions$.next();
    this.clearSubscriptions$.complete();
  }

  public getDataLayer(): unknown[] {
    this.window.dataLayer = this.window.dataLayer || [];

    return this.window.dataLayer;
  }

  public setGtmId(gtmId: string): void {
    this.gtmId = gtmId;
  }

  public setAllowMultipleScripts(multipleScripts: boolean): void {
    this.allowMultiplesScripts = multipleScripts;
  }

  // Referencias: https://github.com/mzuccaroli/angular-google-tag-manager
  public addGtmToDom(): void {
    this.isLoading = true;
    const doc = this.document;

    this.insertGtmStartLayer();

    const gtmScript = doc.createElement('script');
    gtmScript.id = 'GTMscript';
    gtmScript.async = true;
    gtmScript.src = this.applyGtmQueryParams(
      'https://www.googletagmanager.com/gtm.js'
    );
    doc.head.insertBefore(gtmScript, doc.head.firstChild);
    
    this.insertGtmNoscript(doc);

    gtmScript.onload = () => this.isLoad();
  }

  public insertGtmNoscript(doc: Document) {
    const ifrm = doc.createElement('iframe');
    ifrm.setAttribute(
      'src',
      this.applyGtmQueryParams('https://www.googletagmanager.com/ns.html')
    );
    ifrm.style.width = '0';
    ifrm.style.height = '0';
    ifrm.style.display = 'none';
    ifrm.style.visibility = 'hidden';

    const noscript = doc.createElement('noscript');
    noscript.id = 'GTMiframe';
    noscript.appendChild(ifrm);

    doc.body.insertBefore(noscript, doc.body.firstChild);
  }

  public insertGtmStartLayer() {
    const gtmStart: GTMStart = {
      'gtm.start': new Date().getTime(),
      event: 'gtm.js'
    };
    this.pushOnDataLayer(gtmStart);
  }

  public initSsrGtmScript(gtmScript: HTMLScriptElement) {
    this.isLoading = true;
    const doc = this.document;
    this.insertGtmStartLayer();

    this.insertGtmNoscript(doc);
    
    if (doc.readyState === 'complete') {
      this.isLoad();
    } else {
      gtmScript.onload = () => this.isLoad();
    }
  }

  public pushTag(analytics: AnalyticsEcommerceGTMSettings): void {
    if (!this.isLoaded && this.isLoadRequired()) {
      this.eventsToLoad.push(analytics);
      if (!this.isLoading) {
        this.addGtmToDom();
      }
    } else {
      this.pushOnDataLayer(analytics);
    }
  }

  public isLoad(): void {
    this.eventsToLoad.forEach((analyticEvent) => {
      this.pushOnDataLayer(analyticEvent);
    });

    this.eventsToLoad = [];
    this.isLoading = false;
    this.isLoaded = true;
    this.isLoaded$.next(true);
  }

  private isLoadRequired() {
    if (this.allowMultiplesScripts) {
      return true;
    }

    if (this.isLoading) {
      return true;
    }

    const scripts: NodeListOf<HTMLScriptElement> = this.document.querySelectorAll('script');
    let isRequiredToLoad = true;
    if (isDefined(scripts)) {
      Array.prototype.forEach.call(scripts, (script: HTMLScriptElement) => {
        if (script.src.includes('googletagmanager.com/gtm.js')) {
          isRequiredToLoad = false;
          if (script.dataset?.ssr === 'true') {
            this.initSsrGtmScript(script);
          }
        }
      });
    }

    return isRequiredToLoad;
  }

  private pushOnDataLayer(settings: AnalyticsEcommerceGTMSettings | GTMStart) {
    const dataLayer: WindowDataLayer['dataLayer'] = this.getDataLayer();
    dataLayer.push(settings);
  }

  private applyGtmQueryParams(url: string) {
    const params: string[] = [`id=${this.gtmId}`];

    const paramReadyUrl = url + (!url.includes('?') ? '?' : '');

    return paramReadyUrl + params.join('&');
  }

}
